import React, { useState, useEffect } from 'react';
import { useResponsive } from '../utils/useResponsive';

/**
 * Desktop Recommendation Banner
 * Shows a dismissible banner suggesting desktop use for better experience
 */
export const DesktopRecommendationBanner = () => {
  const { isMobile, isTablet } = useResponsive();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed
    const dismissed = localStorage.getItem('desktop-banner-dismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Show banner on mobile/tablet devices
    if (isMobile || isTablet) {
      setIsVisible(true);
    }
  }, [isMobile, isTablet]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('desktop-banner-dismissed', 'true');
  };

  const handleDismissForSession = () => {
    setIsVisible(false);
    // Don't set localStorage, so it shows again on next visit
  };

  if (!isVisible || isDismissed) {
    return null;
  }

  return (
    <div className="desktop-recommendation-banner">
      <div className="banner-content">
        <div className="banner-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none"/>
            <path d="M8 20h8" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 18v2" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
        <div className="banner-text">
          <h4>Better Experience on Desktop</h4>
          <p>For the best experience with all features, we recommend using a desktop or laptop computer.</p>
        </div>
        <div className="banner-actions">
          <button 
            className="banner-btn secondary" 
            onClick={handleDismissForSession}
            aria-label="Continue on mobile for this session"
          >
            Continue
          </button>
          <button 
            className="banner-btn primary" 
            onClick={handleDismiss}
            aria-label="Dismiss banner permanently"
          >
            Got it
          </button>
        </div>
      </div>
      <button 
        className="banner-close" 
        onClick={handleDismiss}
        aria-label="Close banner"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};

/**
 * Mobile Navigation Enhancements
 * Provides better mobile navigation patterns
 */
export const MobileNavEnhancements = ({ children }) => {
  const { isMobile } = useResponsive();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  if (!isMobile) {
    return children;
  }

  return (
    <div className={`mobile-nav-enhanced ${isScrolled ? 'scrolled' : ''}`}>
      {children}
    </div>
  );
};

/**
 * Touch Gesture Handler
 * Adds swipe gestures for mobile navigation
 */
export const TouchGestureHandler = ({ onSwipeLeft, onSwipeRight, children, disabled = false }) => {
  const { isMobile, isTouchDevice } = useResponsive();
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    if (disabled || !isTouchDevice) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    if (disabled || !isTouchDevice) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (disabled || !isTouchDevice || !touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onSwipeLeft) {
      onSwipeLeft();
    }
    if (isRightSwipe && onSwipeRight) {
      onSwipeRight();
    }
  };

  if (!isMobile && !isTouchDevice) {
    return children;
  }

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="touch-gesture-handler"
    >
      {children}
    </div>
  );
};

/**
 * Mobile Table Wrapper
 * Converts tables to mobile-friendly card layouts
 */
export const MobileTableWrapper = ({ children, headers = [], data = [] }) => {
  const { isMobile } = useResponsive();

  if (!isMobile) {
    return children;
  }

  // If data is provided, render as cards
  if (data.length > 0 && headers.length > 0) {
    return (
      <div className="mobile-table-cards">
        {data.map((row, index) => (
          <div key={index} className="mobile-table-card">
            {headers.map((header, headerIndex) => (
              <div key={headerIndex} className="mobile-table-row">
                <div className="mobile-table-label">{header}</div>
                <div className="mobile-table-value">{row[headerIndex]}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Otherwise, wrap the table with horizontal scroll
  return (
    <div className="mobile-table-wrapper">
      <div className="mobile-table-scroll">
        {children}
      </div>
      <div className="mobile-table-hint">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Swipe to see more
      </div>
    </div>
  );
};

/**
 * Mobile Form Enhancements
 * Improves form usability on mobile devices
 */
export const MobileFormEnhancements = ({ children }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (!isMobile) return;

    // Prevent zoom on input focus for iOS
    const addViewportMeta = () => {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    };

    const restoreViewportMeta = () => {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };

    const inputs = document.querySelectorAll('input, select, textarea');
    
    inputs.forEach(input => {
      input.addEventListener('focus', addViewportMeta);
      input.addEventListener('blur', restoreViewportMeta);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', addViewportMeta);
        input.removeEventListener('blur', restoreViewportMeta);
      });
    };
  }, [isMobile]);

  if (!isMobile) {
    return children;
  }

  return (
    <div className="mobile-form-enhanced">
      {children}
    </div>
  );
};

/**
 * Mobile Performance Monitor
 * Monitors and optimizes performance on mobile devices
 */
export const MobilePerformanceMonitor = ({ children }) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (!isMobile) return;

    // Optimize animations for mobile
    const reduceAnimations = () => {
      document.documentElement.style.setProperty('--animation-duration', '0.2s');
      document.documentElement.style.setProperty('--transition-duration', '0.15s');
    };

    // Lazy load images
    const lazyLoadImages = () => {
      const images = document.querySelectorAll('img[data-src]');
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    };

    reduceAnimations();
    lazyLoadImages();

    // Memory cleanup
    return () => {
      // Cleanup observers if needed
    };
  }, [isMobile]);

  return children;
};
